import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ContentTab } from '@/pages/home/aboutLc.content'
import { VideoDialog } from '@/components/common/VideoDialog'
import { HlsVideoPlayer } from '@/components/common/HlsVideoPlayer'

interface ContentTabsSectionProps {
  titleHighDesktop: string
  titlePrimaryDesktop: string
  titleHighMobileA: string
  titleHighMobileB: string
  titlePrimaryMobileA: string
  titlePrimaryMobileB: string
  tabs: ContentTab[]
}

export function ContentTabsSection({
  titleHighDesktop,
  titlePrimaryDesktop,
  titleHighMobileA,
  titleHighMobileB,
  titlePrimaryMobileA,
  titlePrimaryMobileB,
  tabs,
}: ContentTabsSectionProps) {
  const { t: mt } = useTranslation('marketing')

  const [activeTab, setActiveTab] = useState(tabs[0]?.value ?? '')
  const [slideIdx, setSlideIdx] = useState(0)
  const [openVideo, setOpenVideo] = useState<{ src: string; poster: string } | null>(null)

  const current = tabs.find((t) => t.value === activeTab) ?? tabs[0]
  if (!current) return null

  const total = current.slides.length
  const prev = () => setSlideIdx((i) => (i - 1 + total) % total)
  const next = () => setSlideIdx((i) => (i + 1) % total)

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    setSlideIdx(0)
  }

  const handleSlideClick = () => {
    if (current.hasPlayOverlay && slide?.videoUrl) {
      setOpenVideo({ src: slide.videoUrl, poster: slide.url })
    }
  }

  const slide = current.slides[slideIdx]

  return (
    <section id="content-tabs" data-marketing-section="" className="w-full bg-qb-bg-tertiary py-14 lg:py-24 tracking-[-0.6px]">
      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 flex flex-col items-center gap-14 lg:gap-20">
        {/* Title */}
        <h2 className="text-center font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
          <span className="block text-qb-text-high">
            <span className="hidden lg:inline">{titleHighDesktop}</span>
            <span className="lg:hidden">
              {titleHighMobileA}
              <br aria-hidden />
              {titleHighMobileB}
            </span>
          </span>
          <span className="block mt-1 lg:mt-2.5 text-qb-primary">
            <span className="hidden lg:inline">{titlePrimaryDesktop}</span>
            <span className="lg:hidden">
              {titlePrimaryMobileA}
              <br aria-hidden />
              {titlePrimaryMobileB}
            </span>
          </span>
        </h2>

        {/* Content block (tabs + card) */}
        <div className="w-full flex flex-col items-center gap-10">
          {/* Tabs — pill, primary active / blue/100 inactive */}
          <div role="tablist" className="w-full max-w-[360px] lg:max-w-none flex items-center justify-center gap-2 lg:gap-6">
            {tabs.map((t) => {
              const isActive = t.value === activeTab
              return (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleTabChange(t.value)}
                  className={
                    'flex-1 lg:flex-none lg:w-[120px] h-10 lg:h-14 px-2 lg:px-6 rounded-full font-medium text-[16px] lg:text-[20px] leading-[1.5] transition-colors whitespace-nowrap ' +
                    (isActive
                      ? 'bg-qb-primary text-white'
                      : 'bg-[#dceaff] text-qb-text-high hover:bg-[#c7dcff]')
                  }
                >
                  {t.label}
                </button>
              )
            })}
          </div>

          {/* Card panel */}
          <div className="w-full max-w-[360px] lg:max-w-none lg:w-[912px] bg-qb-bg rounded-[24px] lg:rounded-[32px] shadow-[0px_8px_12px_rgba(0,0,0,0.05)] px-6 lg:px-6 pt-8 lg:pt-12 pb-8 flex flex-col items-center gap-10 lg:gap-14">
            {/* Text */}
            <div className="flex flex-col items-center gap-2 text-center">
              <p className="font-medium text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4] text-black/80">
                {current.panelTitle}
              </p>
              <p className="font-bold text-qb-text-high text-[24px] lg:text-[28px] leading-[1.4]">
                {current.panelSubline}
              </p>
            </div>

            {/* Slider */}
            <div className="w-full flex flex-col items-center gap-6">
              <div className="w-full flex items-center justify-center gap-4 lg:gap-10">
                {/* Left chevron — desktop only */}
                <button
                  type="button"
                  onClick={prev}
                  aria-label={mt('navigation.previousSlide')}
                  className="hidden lg:flex shrink-0 w-12 h-12 rounded-full items-center justify-center bg-qb-bg-secondary text-qb-text-mid hover:bg-[#dceaff] hover:text-qb-primary transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                {/* Slide */}
                <button
                  type="button"
                  onClick={handleSlideClick}
                  disabled={!current.hasPlayOverlay}
                  className="relative block w-full lg:w-[638px] overflow-hidden rounded-[16px] lg:rounded-[24px] bg-qb-bg disabled:cursor-default"
                  style={{ aspectRatio: '638/360' }}
                >
                  {slide && (
                    <img
                      src={slide.url}
                      alt={`${current.label} ${slideIdx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {current.hasPlayOverlay && (
                    <span
                      aria-hidden
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
                        <span className="ml-1 border-y-[8px] border-y-transparent border-l-[14px] border-l-white lg:border-y-[10px] lg:border-l-[16px]" />
                      </span>
                    </span>
                  )}
                </button>

                {/* Right chevron — desktop only */}
                <button
                  type="button"
                  onClick={next}
                  aria-label={mt('navigation.nextSlide')}
                  className="hidden lg:flex shrink-0 w-12 h-12 rounded-full items-center justify-center bg-qb-bg-secondary text-qb-text-mid hover:bg-[#dceaff] hover:text-qb-primary transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Pagination dots — active = pill, inactive = circle */}
              <div className="flex items-center gap-2">
                {current.slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSlideIdx(i)}
                    aria-label={mt('media.slide', { number: i + 1 })}
                    className={
                      i === slideIdx
                        ? 'w-6 h-2 rounded-full bg-[#131313] lg:bg-qb-primary'
                        : 'w-2 h-2 rounded-full bg-qb-gray-300'
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <VideoDialog
        open={openVideo !== null}
        onOpenChange={(open) => {
          if (!open) setOpenVideo(null)
        }}
      >
        {openVideo && (
          <HlsVideoPlayer
            key={openVideo.src}
            src={openVideo.src}
            poster={openVideo.poster}
          />
        )}
      </VideoDialog>
    </section>
  )
}
